import type { CollectionPreferencesProps } from '@risk-smart/themed-cloudscape-components/collection-preferences';
import {
  arrayToCsv,
  downloadBlob,
} from '@risksmart-app/components/src/file/fileUtils';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { ReportingDataInput } from '@risksmart-app/shared/reporting/api/schema';
import type {
  GetFormCustomisationQuery,
  GetReportingDataQuery,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCustomDatasourceExporterMapper } from 'src/pages/custom-datasources/detail/customDatasourceExporter';
import type { TypedCustomDatasource } from 'src/pages/custom-datasources/types';
import type { CustomAttributeSchemaLookup } from 'src/pages/custom-datasources/update/types';

import { useLazyGetReportingData } from '@/hooks/queries';
import { handleError } from '@/utils/errorUtils';
import { collectExportRows, fetchChunkOutcome } from '@/utils/exportChunking';

import { toExportFilename } from '../../../utils/exportFilename';
import { useDashboardBulkExportStore } from '../useDashboardBulkExportStore';
import {
  type ExportFn,
  useWidgetContext,
} from '../widget-context/WidgetContext';
import type { ChartType } from './form/customDataSourceWidgetSettingsSchema';

// Lambda sync invocation caps response payloads at ~6 MB, so the export pages
// through the data instead of requesting every row in one call — mirroring the
// standalone custom-datasource export (custom-datasources/detail/Page.tsx).
const EXPORT_ROW_CAP = 100000;

interface Params {
  chartType: ChartType | undefined;
  /** Settings are valid and a datasource is loaded — safe to build a query. */
  enabled: boolean;
  loading: boolean;
  title: string | undefined;
  /**
   * The live reporting query the widget renders with (paging is overridden per
   * chunk during export). Undefined until the datasource resolves.
   */
  reportDataDefinition: ReportingDataInput | undefined;
  customDatasource:
    | Pick<TypedCustomDatasource, 'Datasources' | 'Fields'>
    | undefined;
  customAttributeSchemaLookup: CustomAttributeSchemaLookup | null | undefined;
  formFieldConfigurations:
    | GetFormCustomisationQuery['form_field_configuration']
    | null;
  /** Persisted column preferences (order + visibility) — mirrors on-screen order. */
  contentDisplay?:
    | readonly CollectionPreferencesProps.ContentDisplayItem[]
    | undefined;
}

/**
 * Wires CSV export for a custom-datasource *table* widget (RSP-6025): a
 * per-widget "Download CSV" action plus a CSV contribution to the full-dashboard
 * zip export. Inactive for non-table chart types, whose export is owned by the
 * chart renderer.
 */
export const useCustomDataSourceWidgetExport = ({
  chartType,
  enabled,
  loading,
  title,
  reportDataDefinition,
  customDatasource,
  customAttributeSchemaLookup,
  formFieldConfigurations,
  contentDisplay,
}: Params): void => {
  const widgetData = useWidgetContext();
  const { addWidgetExport, removeWidgetExport } = useDashboardBulkExportStore();
  const { addNotification } = useNotifications();
  const { t } = useTranslation(['common'], { keyPrefix: 'export' });
  const { t: rt } = useTranslation(['common']);

  const formatForExport = useCustomDatasourceExporterMapper({
    customDatasource,
    customAttributeSchemaLookup,
    formFieldConfigurations,
    contentDisplay,
  });

  const [getReportData] = useLazyGetReportingData();

  // Volatile values are read through refs so the export callbacks stay stable
  // (they rebuild on every render) and don't thrash the registration effects.
  const reportDataDefinitionRef = useRef(reportDataDefinition);
  reportDataDefinitionRef.current = reportDataDefinition;
  const formatForExportRef = useRef(formatForExport);
  formatForExportRef.current = formatForExport;
  // `useNotifications` returns a brand-new `addNotification` every render, and
  // react-i18next can hand back a fresh `t`. Reading both through refs keeps
  // `buildCsvString` — and everything memoised from it (`downloadCsv`,
  // `csvExportFns`) — referentially stable, so the registration effects don't
  // re-run on every render and thrash parent/store state into an update loop.
  const addNotificationRef = useRef(addNotification);
  addNotificationRef.current = addNotification;
  const rtRef = useRef(rt);
  rtRef.current = rt;
  // Filename follows the on-screen title, falling back to the widget
  // definition's title (matching the chart export) then a generic default.
  const exportTitle = title ?? widgetData?.title;
  const titleRef = useRef(exportTitle);
  titleRef.current = exportTitle;

  const buildCsvString = useCallback(async (): Promise<string> => {
    const definition = reportDataDefinitionRef.current;
    if (!definition) {
      return '';
    }

    try {
      // CUS-314: shared width-aware paging — see `@/utils/exportChunking`.
      const columnCount = definition.fields.length;
      const result = await collectExportRows<
        NonNullable<GetReportingDataQuery['reportingData']>[number]
      >({
        columnCount,
        rowCap: EXPORT_ROW_CAP,
        fetchChunk: async ({ offset, limit }) =>
          fetchChunkOutcome(
            () => getReportData({ ...definition, offset, limit }),
            (data) => data.reportingData ?? []
          ),
      });

      if (result.status !== 'complete') {
        if (result.status === 'failed') {
          handleError(
            result.failure,
            {
              extra: {
                columnCount,
                dataSourceCount: definition.dataSources.length,
                offset: result.offset,
                limit: result.limit,
                rowsCollected: result.rowsCollected,
              },
            },
            'customDatasourceWidget.exportToCsv'
          );
          addNotificationRef.current({
            type: 'error',
            content: rtRef.current('export.export_failed_message'),
          });
        }

        return '';
      }

      if (result.truncated) {
        // Typed `error`, not a softer level: the notification API offers only
        // error / success, and `error` is the one that renders persistently
        // instead of auto-dismissing after 3.5s. A truncation notice the user
        // can miss is worse than none — they would keep an incomplete file
        // believing it complete.
        addNotificationRef.current({
          type: 'error',
          content: rtRef.current('export.export_truncated_message', {
            count: EXPORT_ROW_CAP,
          }),
        });
      }

      return arrayToCsv(formatForExportRef.current(result.rows));
    } catch (e) {
      handleError(e, undefined, 'customDatasourceWidget.exportToCsv');
      addNotificationRef.current({
        type: 'error',
        content: rtRef.current('export.export_failed_message'),
      });

      return '';
    }
  }, [getReportData]);

  const downloadCsv = useCallback(async (): Promise<void> => {
    const csv = await buildCsvString();
    if (!csv) {
      return;
    }
    // '\ufeff' is the UTF-8 BOM so non-ASCII characters render correctly in Excel.
    const blob = new Blob(['\ufeff', csv], {
      type: 'text/csv;charset=utf-8',
    });
    downloadBlob(`${toExportFilename(titleRef.current)}.csv`, blob);
  }, [buildCsvString]);

  const isTableExport = chartType === 'table' && enabled;

  // The useState setter from WidgetContext is referentially stable, and the
  // export-fn array is memoised, so registering does NOT feed back into this
  // effect — `setExportFns` updates the context value on every call, which
  // would otherwise change `widgetData`'s identity and re-run a `widgetData`-
  // keyed effect (and its cleanup), looping. Depend on the stable setter and a
  // stable array instead.
  const setExportFns = widgetData?.setExportFns;
  // String() narrows i18next's typed-key union return to string. Key the memo
  // on the resolved label (a primitive) and the now-stable `downloadCsv` rather
  // than on `t`'s identity, so `csvExportFns` keeps a stable reference even when
  // react-i18next returns a fresh `t` each render.
  const downloadCsvLabel = String(t('download_csv', 'Download CSV'));
  const csvExportFns = useMemo<ExportFn[]>(
    () => [
      {
        id: 'csv',
        text: downloadCsvLabel,
        fn: () => {
          void downloadCsv();
        },
      },
    ],
    [downloadCsvLabel, downloadCsv]
  );

  // Per-widget export button: register a "Download CSV" action while this is a
  // ready table, and clear it when leaving the table view so a stale CSV action
  // can't linger on a chart/kpi variant.
  useEffect(() => {
    if (!setExportFns || !isTableExport || loading) {
      return;
    }
    setExportFns(csvExportFns);

    return () => setExportFns([]);
  }, [isTableExport, loading, setExportFns, csvExportFns]);

  // Full-dashboard zip: contribute a CSV entry alongside the chart images.
  const widgetId = widgetData?.widgetId;
  useEffect(() => {
    if (!widgetId || !isTableExport || loading) {
      return;
    }
    // `exportTitle` is a reactive dep so the entry re-registers with the latest
    // filename if the widget title changes; the CSV itself is built lazily.
    addWidgetExport({
      id: widgetId,
      filename: toExportFilename(exportTitle),
      exportFn: async () => ({ kind: 'csv', csv: await buildCsvString() }),
    });

    return () => removeWidgetExport(widgetId);
  }, [
    widgetId,
    isTableExport,
    loading,
    exportTitle,
    addWidgetExport,
    removeWidgetExport,
    buildCsvString,
  ]);
};
